import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FilterListIcon from '@mui/icons-material/FilterList';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineOppositeContent,
  TimelineSeparator,
} from '@mui/lab';
import { timelineOppositeContentClasses } from '@mui/lab/TimelineOppositeContent';
import {
  Pagination, Popper, Stack, Typography,
} from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import React, { useContext, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import postBrandGroupLogs from '../../../../../api/oc_product/brandGroup/logs/postBrandGroupLogs';
import parseDateString from '../../../../../utils/parseDateString';
import { BrandGroupingContext } from '../BrandGroupingContext';

function BrandGroupingLogs() {
  const siteId = useSelector((state) => state.user.loginResponse?.selectedSite?.SiteId);
  const { reFetchLogs, setReFetchLogs } = useContext(BrandGroupingContext);
  const { id } = useParams();

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [logs, setLogs] = useState([]);
  const [totalPages, setTotalPages] = useState();
  const [totalRows, setTotalRows] = useState(0);

  const [username, setUsername] = useState(''.toLowerCase());
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [resetData, setResetData] = useState(false);
  const [openMoreFilters, setOpenMoreFilters] = useState(false);

  const [expandedLogs, setExpandedLogs] = useState([]);
  const isLogsOpen = true;
  const fetchLogs = async () => {
    try {
      const logsData = await postBrandGroupLogs(
        siteId,
        currentPage,
        itemsPerPage,
        {
          fromDate,
          toDate,
          userName: username,
        },
        id,
      );

      setLogs(logsData.data.Data);
      setTotalPages(logsData.data.Pagination.PageCount);
      setTotalRows(logsData.data.Summary.TotalRowCount);
      setReFetchLogs(false);
    } catch (error) {
      console.error('Error fetching brand group logs:', error);
      setLogs([]);
      setTotalPages(0);
      setTotalRows(0);
    }
  };

  const handleChangePage = (event, newPage) => {
    setCurrentPage(newPage);
  };

  const handleFilterFromDateChange = (selectedDate) => {
    setFromDate(selectedDate);
  };

  const handleFilterToDateChange = (selectedDate) => {
    const endOfDay = selectedDate ? new Date(selectedDate.setHours(23, 59, 59, 999)) : null;
    setToDate(endOfDay);
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchLogs();
    setResetData(false);
    setOpenMoreFilters(null);
  };

  const handleFilterReset = () => {
    setUsername('');
    setFromDate(null);
    setToDate(null);
    setCurrentPage(1);
    setResetData(true);
  };

  const handleChangeItemsPerPage = (event) => {
    const newItemsPerPage = event.target.value;
    const newPageCount = Math.max(1, Math.ceil(totalRows / newItemsPerPage));
    const newPage = Math.min(currentPage, newPageCount);

    setItemsPerPage(newItemsPerPage);
    setCurrentPage(newPage);
  };

  const handleExpandClick = (logId) => {
    setExpandedLogs((prevExpandedLogs) => {
      if (prevExpandedLogs.includes(logId)) {
        return prevExpandedLogs.filter((log) => log !== logId);
      }
      return [...prevExpandedLogs, logId];
    });
  };

  const handleOpenMoreFilter = (event) => {
    setOpenMoreFilters(event.currentTarget);
  };

  const handleCloseMoreFilter = () => {
    setOpenMoreFilters(null);
  };

  const handleClickAway = () => {
    handleCloseMoreFilter();
  };
  const open = Boolean(openMoreFilters);

  React.useEffect(() => {
    if (isLogsOpen) {
      fetchLogs();
    }
  }, [siteId, isLogsOpen, id]);

  React.useEffect(() => {
    if (currentPage > 0 && itemsPerPage > 0 && isLogsOpen) {
      fetchLogs();
    }
  }, [currentPage, itemsPerPage]);

  React.useEffect(() => {
    if (resetData && isLogsOpen) {
      fetchLogs();
      setResetData(false);
    }
  }, [resetData]);

  React.useEffect(() => {
    if (reFetchLogs && isLogsOpen) {
      fetchLogs();
    }
  }, [reFetchLogs]);

  React.useEffect(() => {
  }, [fromDate, toDate]);

  React.useEffect(() => {
  }, [username, isLogsOpen]);

  const formatLogContent = (message) => {
    const jsonStartIndex = message.indexOf('[');
    const jsonEndIndex = message.lastIndexOf(']') + 1;
    const hasJson = jsonStartIndex !== -1 && jsonEndIndex !== 0 && jsonEndIndex > jsonStartIndex;
    const nonJsonMessage = hasJson ? message.substring(0, jsonStartIndex).trim() : message.trim();

    return { nonJsonMessage, hasJson };
  };

  return (
    <div className="relative">
      <Box
        className="h-[calc(100vh-120px)] border rounded bg-neutral-100 relative
          transition-all ease-in-out duration-300"
        style={{
          right: isLogsOpen ? '0' : '-100%',
          overflow: 'hidden',
        }}
      >
        {isLogsOpen && (
          <section className="h-full flex flex-col">

            <div className="p-2 m-1 bg-white border rounded-lg shadow-md flex-shrink-0">
              <div className="flex flex-col items-center sm:flex-row gap-2">

                <TextField
                  label="Name"
                  variant="outlined"
                  value={username}
                  size="small"
                  onChange={(event) => setUsername(event.target.value.toLowerCase())}
                  className="w-full sm:w-[250px]"
                />

                <Button
                  id="more-filters-button"
                  onClick={handleOpenMoreFilter}
                  variant="text"
                  className="text-[10px] w-[70px] text-primary-600 sm:mt-0 sm:ml-[5px] min-w-[42px] w-10"
                >
                  <Tooltip
                    title={(
                      <p className="text-sm">More Filters</p>
                    )}
                  >
                    <FilterListIcon />
                  </Tooltip>
                </Button>

                {open && (
                  <ClickAwayListener onClickAway={handleClickAway}>
                    <Popper
                      open={open}
                      anchorEl={openMoreFilters}
                      placement="right-start"
                      className="absolute z-10 p-4 bg-white border rounded-lg shadow-xl right-4 top-10 w-80"
                    >
                      <Typography className="text-primary-600 pb-2 font-semibold" variant="subtitle1">More Filters</Typography>

                      <DatePicker
                        label="From"
                        inputFormat="dd-MM-yyyy"
                        value={fromDate}
                        onChange={handleFilterFromDateChange}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            className="w-full mb-4"
                          />
                        )}
                        showDaysOutsideCurrentMonth
                        maxDate={toDate || new Date()}
                        className="w-full mb-4"
                      />

                      <DatePicker
                        label="To"
                        inputFormat="dd-MM-yyyy"
                        value={toDate}
                        onChange={handleFilterToDateChange}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            className="w-full mb-4"
                          />
                        )}
                        showDaysOutsideCurrentMonth
                        minDate={fromDate}
                        maxDate={new Date()}
                        className="w-full mb-4"
                      />

                      <Stack direction="row" spacing={1} justifyContent="flex-end" className="pt-2">
                        <Button
                          onClick={handleSearch}
                          variant="contained"
                          disabled={username === '' && !fromDate && !toDate}
                        >
                          Apply
                        </Button>
                        <Button
                          onClick={() => {
                            setFromDate(null);
                            setToDate(null);
                            handleCloseMoreFilter();
                          }}
                          variant="text"
                          className="text-primary-600"
                          disabled={!fromDate && !toDate}
                          sx={{
                            '&.Mui-disabled': {
                              WebkitTextFillColor: 'rgba(0, 0, 0, 0.26)',
                            },
                          }}
                        >
                          Clear
                        </Button>
                      </Stack>
                    </Popper>
                  </ClickAwayListener>
                )}

                <Button
                  onClick={handleSearch}
                  variant="contained"
                  className="w-full sm:w-auto text-sm pl-[22px] pr-[22px]"
                  disabled={username === '' && !fromDate && !toDate}
                >
                  Search
                </Button>

                <Button
                  onClick={handleFilterReset}
                  variant="text"
                  className="text-primary-600 w-full sm:w-auto text-sm"
                >
                  Reset
                </Button>
              </div>
            </div>

            <div className="py-2 overflow-y-auto flex-grow px-2">
              {logs.length > 0 ? (
                <Timeline
                  className="mb-10"
                  sx={{
                    [`& .${timelineOppositeContentClasses.root}`]: {
                      flex: 'none',
                      width: '100px',
                      padding: '2px 8px 2px 0',
                      textAlign: 'right',
                    },
                    '& .MuiTimelineItem-missingOppositeContent:before': {
                      flex: 'none',
                      padding: 0,
                    },
                    '& .MuiTimelineItem-root': {
                      paddingTop: '4px',
                      paddingBottom: '4px',
                      minHeight: 'auto',
                      position: 'relative',
                    },
                  }}
                >
                  {logs.map((log) => {
                    const logId = log.Created;
                    const { nonJsonMessage, hasJson } = formatLogContent(log.Message);
                    return (
                      <TimelineItem
                        key={logId}
                        sx={{
                          alignItems: 'flex-start',
                          minHeight: '40px',
                        }}
                      >
                        <TimelineOppositeContent
                          sx={{
                            padding: '2px 8px 2px 0',
                            fontSize: '0.875rem',
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ color: '#6f7686', fontWeight: 500 }}
                          >
                            {parseDateString(log.Created)}
                          </Typography>
                        </TimelineOppositeContent>

                        <TimelineSeparator
                          sx={{
                            paddingY: '2px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            minHeight: '40px',
                          }}
                        >
                          <div style={{ position: 'relative', width: '20px', height: '40px' }}>
                            <TimelineDot
                              sx={{
                                backgroundColor: 'transparent',
                                color: '#1aa0b3',
                                fontSize: '0.875rem',
                                margin: '0',
                                padding: '0',
                                lineHeight: '1',
                                boxShadow: 'none',
                                border: 'none',
                                position: 'absolute',
                                top: '0',
                              }}
                            >
                              <CheckCircleIcon sx={{ fontSize: '18px', color: '#1aa0b3' }} />
                            </TimelineDot>
                            <TimelineConnector
                              sx={{
                                backgroundColor: '#1aa0b3',
                                width: '2px',
                                height: '52px',
                                position: 'absolute',
                                top: '18px',
                                left: '50%',
                                transform: 'translateX(-50%)',
                              }}
                            />
                          </div>
                        </TimelineSeparator>

                        <TimelineContent
                          sx={{
                            padding: '2px 0 4px 10px',
                            minWidth: '250px',
                            flexGrow: 1,
                          }}
                        >
                          <Typography variant="body2" sx={{ color: '#000000', lineHeight: 1.2 }}>
                            {nonJsonMessage}
                          </Typography>

                          {hasJson && !expandedLogs.includes(logId) && (
                            <Typography
                              variant="body2"
                              className="cursor-pointer"
                              sx={{
                                color: '#1aa0b3',
                                marginBottom: '4px',
                                marginTop: '4px',
                                fontSize: '0.875rem',
                                '&:hover': { textDecoration: 'underline' },
                              }}
                              onClick={() => handleExpandClick(logId)}
                            >
                              Show more details
                            </Typography>
                          )}

                          {expandedLogs.includes(logId) && hasJson && (
                            <Box className="mt-1 mb-2">
                              <Typography
                                variant="body2"
                                className="cursor-pointer"
                                sx={{
                                  color: '#1aa0b3',
                                  marginBottom: '4px',
                                  marginTop: '4px',
                                  fontSize: '0.875rem',
                                  '&:hover': { textDecoration: 'underline' },
                                }}
                                onClick={() => handleExpandClick(logId)}
                              >
                                Show less details
                              </Typography>
                              <ul className="list-disc pl-5 text-xs text-gray-600">
                                {(() => {
                                  const jsonStartIndex = log.Message.indexOf('[');
                                  const jsonEndIndex = log.Message.lastIndexOf(']') + 1;

                                  if (jsonStartIndex !== -1 && jsonEndIndex !== 0) {
                                    const rawContent = log.Message.substring(jsonStartIndex, jsonEndIndex);
                                    return <li className="py-0.5 break-words">{rawContent}</li>;
                                  }

                                  return <li className="py-0.5 break-words">{log.Message}</li>;
                                })()}
                              </ul>
                            </Box>
                          )}

                          <Typography
                            variant="caption"
                            sx={{
                              color: '#6f7686', display: 'flex', alignItems: 'center', marginTop: '4px',
                            }}
                          >
                            <PersonOutlineIcon sx={{ fontSize: '16px', marginRight: '4px' }} />
                            {log.UserName}
                          </Typography>
                        </TimelineContent>
                      </TimelineItem>
                    );
                  })}
                </Timeline>
              ) : <Typography className="pl-4 py-4">No logs found matching the current criteria.</Typography>}
            </div>

            <div className="flex flex-col items-center gap-6 p-2 pl-4 bg-white border rounded-lg sm:flex-row w-[98%]
              flex-shrink-0 m-1 shadow-md justify-between"
            >
              <FormControl variant="standard" size="small" className="flex flex-row items-center">
                <Typography variant="body2" className="mr-4 text-sm text-gray-700">Rows per page:</Typography>
                <Select
                  labelId="logs-per-page"
                  value={itemsPerPage}
                  onChange={handleChangeItemsPerPage}
                  autoWidth
                  variant="standard"
                  className="text-sm"
                  MenuProps={{ disableScrollLock: true }}
                >
                  <MenuItem value={5}>5</MenuItem>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={15}>15</MenuItem>
                </Select>
              </FormControl>

              <Pagination
                count={totalPages || 1}
                page={currentPage}
                onChange={handleChangePage}
                size="medium"
                sx={{ visibility: totalPages > 1 ? 'visible' : 'hidden' }}
              />
            </div>
          </section>
        )}
      </Box>
    </div>
  );
}

export default BrandGroupingLogs;
